/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, {
  useEffect,
  useContext,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Col, Row } from "react-bootstrap";
import { Alert, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { IoCopy, IoInformationCircleSharp } from "react-icons/io5";
import { FiPlus, FiMinus } from "react-icons/fi";
import { AppContext } from "../../../../scripts/context";
import { usePresale } from "../../../../scripts/usePresale";
import { copyToClipboard } from "../../../../Helper/Helper";
import { EFFECT_TIME } from "../../../../constants";
import { request } from "../../../../admin/plugins/axios";
import { endpoints } from "../../../../utils/networks";
import avatar from "../../../../assets/avatar.png";
import trtLogo from "../../../../assets/trt.png";
import "./style.css";

const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

function Buy() {
  const {
    address,
    balance,
    telv,
    setTelv,
    sharePriceBnb,
    sharePriceWei,
    userInfo,
    purchase,
    loading,
    error,
    setError,
    success,
    setSuccess,
    determineReferrer,
    verifyReferrer,
    checkingReferrer,
  } = usePresale();

  const {
    userData,
    state: { user = {} } = {},
    userDataSponsor,
    userSponsorAddress,
  } = useContext(AppContext) || {};

  const [validatedSponsor, setValidatedSponsor] = useState({
    address: null,
    valid: false,
  });
  const [activeSponsorData, setActiveSponsorData] = useState(null);
  const [loadingSponsorData, setLoadingSponsorData] = useState(false);

  const bnbAdressShort = useMemo(() => {
    if (!address) return "--";
    return `${address.substring(0, 6)}...${address.substring(
      address.length - 4
    )}`;
  }, [address]);

  const contractSponsor = useMemo(() => {
    if (!userInfo?.referrer || userInfo.referrer === ZERO_ADDRESS) return null;
    // Don't use referrer if it's the user's own address
    if (address && userInfo.referrer.toLowerCase() === address.toLowerCase()) {
      return null;
    }
    return userInfo.referrer;
  }, [userInfo, address]);

  // Get sponsor from URL parameter directly if userDataSponsor is not loaded yet
  const [sponsorFromUrl, setSponsorFromUrl] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("sponsor");
  });

  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const sponsor = urlParams.get("sponsor");
      setSponsorFromUrl(sponsor);
    };

    // Check on mount
    handleUrlChange();

    // Listen for popstate events (back/forward navigation)
    window.addEventListener("popstate", handleUrlChange);

    return () => {
      window.removeEventListener("popstate", handleUrlChange);
    };
  }, []);

  // Priority: sponsorFromUrl (from URL), then userDataSponsor (from URL param), then user's sponsor from profile API
  const sponsorFromApi =
    sponsorFromUrl || userDataSponsor?.wallet_address || userSponsorAddress || null;

  useEffect(() => {
    // Skip validation if sponsorFromApi is the user's own address
    if (!sponsorFromApi) {
      setValidatedSponsor({ address: null, valid: false });
      return;
    }

    // Skip validation if sponsor is the user's own address
    if (address && sponsorFromApi.toLowerCase() === address.toLowerCase()) {
      setValidatedSponsor({ address: null, valid: false });
      return;
    }

    let cancelled = false;
    (async () => {
      const isValid = await verifyReferrer(sponsorFromApi);
      if (!cancelled) {
        setValidatedSponsor({ address: sponsorFromApi, valid: isValid });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sponsorFromApi, verifyReferrer, address]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), EFFECT_TIME);
      return () => clearTimeout(timer);
    }
  }, [error, setError]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), EFFECT_TIME);
      return () => clearTimeout(timer);
    }
  }, [success, setSuccess]);

  const activeSponsor = useMemo(() => {
    // Debug logging
    console.log("🔍 activeSponsor calculation:", {
      contractSponsor,
      validatedSponsor,
      sponsorFromApi,
      sponsorFromUrl,
      address,
      userDataSponsor: userDataSponsor?.wallet_address,
    });

    // First priority: on-chain sponsor retrieved from getMyInfo
    if (contractSponsor) {
      if (address && contractSponsor.toLowerCase() !== address.toLowerCase()) {
        console.log("✅ Using contractSponsor:", contractSponsor);
        return contractSponsor;
      }
    }
    // Second priority: validated sponsor from URL/API if it passed on-chain verification
    if (validatedSponsor.valid && validatedSponsor.address) {
      if (
        address &&
        validatedSponsor.address.toLowerCase() !== address.toLowerCase()
      ) {
        console.log("✅ Using validatedSponsor:", validatedSponsor.address);
        return validatedSponsor.address;
      }
    }
    // Third priority: sponsor from URL directly (highest priority for URL param)
    // This allows showing the sponsor address while verification is in progress
    if (sponsorFromUrl) {
      // Make sure it's not the user's own address
      if (address && sponsorFromUrl.toLowerCase() !== address.toLowerCase()) {
        console.log("✅ Using sponsorFromUrl:", sponsorFromUrl);
        return sponsorFromUrl;
      }
    }
    // Fourth priority: sponsor from API (if exists and not user's address)
    if (sponsorFromApi) {
      // Make sure it's not the user's own address
      if (address && sponsorFromApi.toLowerCase() !== address.toLowerCase()) {
        console.log("✅ Using sponsorFromApi:", sponsorFromApi);
        return sponsorFromApi;
      }
    }
    console.log("❌ No valid sponsor found");
    return null;
  }, [
    contractSponsor,
    validatedSponsor,
    address,
    sponsorFromApi,
    sponsorFromUrl,
    userDataSponsor,
  ]);

  // Fetch sponsor data from API based on activeSponsor
  useEffect(() => {
    if (!activeSponsor) {
      setActiveSponsorData(null);
      return;
    }

    // If userDataSponsor matches activeSponsor, use it (no need to fetch)
    if (
      userDataSponsor?.wallet_address?.toLowerCase() ===
      activeSponsor.toLowerCase()
    ) {
      setActiveSponsorData(userDataSponsor);
      return;
    }

    // Otherwise, fetch sponsor data from API
    let cancelled = false;
    const fetchSponsorData = async () => {
      setLoadingSponsorData(true);
      try {
        const response = await request(
          endpoints.ProfileData.GetProfileData.method,
          `${endpoints.ProfileData.GetProfileData.url}?wallet_address=${activeSponsor}`
        );
        if (!cancelled && response?.data?.data?.user) {
          setActiveSponsorData(response.data.data.user);
        }
      } catch (error) {
        console.error("Failed to fetch sponsor data:", error);
        if (!cancelled) {
          setActiveSponsorData(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingSponsorData(false);
        }
      }
    };

    fetchSponsorData();
    return () => {
      cancelled = true;
    };
  }, [activeSponsor, userDataSponsor]);

  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleCopy = () => {
    if (!address) return;
    copyToClipboard(address);
  };

  const handleSelectPackage = (pkg) => {
    setTelv(pkg);
  };

  const handlePurchase = async () => {
    await purchase({
      shares: telv,
      referrerOverride: validatedSponsor.valid
        ? validatedSponsor.address
        : undefined,
    });
  };

  const scrollIntoView = useCallback(() => {
    const card = document.getElementById("buy-section-card");
    if (card) {
      const offset =
        card.getBoundingClientRect().top + window.pageYOffset - 120;
      window.scrollTo({
        top: offset,
        behavior: "smooth",
      });
    }
  }, []);

  const toggleDetails = useCallback(
    (event) => {
      event?.stopPropagation();
      setDetailsOpen((prev) => {
        const next = !prev;
        if (!prev && next) {
          setTimeout(scrollIntoView, 50);
        }
        return next;
      });
    },
    [scrollIntoView]
  );

  const handleCardClick = () => {
    if (!detailsOpen) {
      setDetailsOpen(true);
      setTimeout(scrollIntoView, 50);
    }
  };

  const priceDisplay = sharePriceBnb ? sharePriceBnb.toFixed(6) : "0";

  return (
    <Row
      className={`bg-live align-items-center justify-content-center shared-margin shared-space h-100 ${
        detailsOpen ? "expanded" : ""
      }`}
      id="buy-section-card"
    >
      <Col
        xs={10}
        className={`live-buy-section ${detailsOpen ? "expanded" : ""}`}
        onClick={handleCardClick}
      >
        <div className="bnb-address d-flex justify-content-between align-items-center">
          <p>Balance: {Number(balance || 0).toFixed(4)} BNB</p>
          <div>
            <span className="mr-2">{bnbAdressShort}</span>
            <IoCopy className="cursor-pointer" onClick={handleCopy} />
          </div>
        </div>
        <h1 className="buy-heading">
          <img
            src={trtLogo}
            alt="Trinity Tether"
            className="buy-heading-logo"
          />
          Buy Trinity Tether
        </h1>
        <div className="buy-toggle-wrapper">
          <button
            type="button"
            className={`buy-toggle-btn ${detailsOpen ? "open" : ""}`}
            onClick={toggleDetails}
            aria-label={detailsOpen ? "Collapse details" : "Expand details"}
          >
            {detailsOpen ? (
              <FiMinus className="buy-toggle-icon" />
            ) : (
              <FiPlus className="buy-toggle-icon" />
            )}
          </button>
        </div>

        <div className={`buy-details ${detailsOpen ? "open" : ""}`}>
          <div className="presal-amount mb-4">
            <h6 className="d-flex align-self-start mb-2">
              Select package <span className="minAllo-span">Shares</span>
            </h6>

            <div className="amount-filter-buttons mb-2">
              <button
                type="button"
                className={`amount-filter-btn ${
                  Number(telv) === 5 ? "active" : ""
                }`}
                onClick={() => setTelv(5)}
              >
                5 shares for $40
              </button>
              <button
                type="button"
                className={`amount-filter-btn ${
                  Number(telv) === 15 ? "active" : ""
                }`}
                onClick={() => setTelv(15)}
              >
                15 shares for $120
              </button>
            </div>

            {/* <h4 className="referral-gain-title">
              Gain through Three Levels of referrals
            </h4> */}

            <div className="amount-filter-buttons referral-buttons mb-4">
              <button
                type="button"
                className={`amount-filter-btn ${
                  Number(telv) === 50 ? "active" : ""
                }`}
                onClick={() => setTelv(50)}
              >
                50 shares for $400
              </button>
              <button
                type="button"
                className={`amount-filter-btn ${
                  Number(telv) === 100 ? "active" : ""
                }`}
                onClick={() => setTelv(100)}
              >
                100 shares for $800
              </button>
              <button
                type="button"
                className={`amount-filter-btn ${
                  Number(telv) === 250 ? "active" : ""
                }`}
                onClick={() => setTelv(250)}
              >
                250 shares for $2000
              </button>
            </div>

            {/* <div className="d-flex justify-content-between align-items-center pb-0">
              <input
                type="number"
                value={telv || ""}
                onChange={(e) => setTelv(Number(e.target.value))}
                min={1}
                className="setNFTHandlerInput"
              /> */}
            {/* <Tooltip
                title="Price is fetched from the contract based on the selected package"
                placement="top"
                arrow
              >
                <span>{priceDisplay} BNB</span>
              </Tooltip> */}
            {/* </div> */}
            <hr />
          </div>

          <div className="fees-data">
            {/* <div>
              <h6>Total Shares Purchased</h6>
              <h2>{userInfo?.totalShares || 0}</h2>
            </div> */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6>
                Payment Amount{" "}
                <Tooltip
                  title="This value is returned from the smart contract and excludes gas fees."
                  placement="top"
                  arrow
                >
                  <IconButton className="totalPriceH2">
                    <IoInformationCircleSharp />
                  </IconButton>
                </Tooltip>
              </h6>
              <Tooltip
                title="Price is fetched from the contract based on the selected package"
                placement="top"
                className="totalPriceH2"
                arrow
              >
                <span>{priceDisplay} BNB</span>
              </Tooltip>
              {/* <h2>{priceDisplay} BNB</h2> */}
            </div>
          </div>

          <div className="bnb-address-sponsor d-flex justify-content-between align-items-center">
            <h6>Sponsor Address</h6>
            <div className="bnb-address-sponsor-content">
              {(checkingReferrer || loadingSponsorData) && (
                <CircularProgress size={18} className="mr-2" />
              )}
              <span className="mr-2">
                {activeSponsor &&
                address &&
                activeSponsor.toLowerCase() !== address.toLowerCase()
                  ? `${activeSponsor.slice(0, 6)}...${activeSponsor.slice(-4)}`
                  : "None"}
              </span>
              {activeSponsor &&
                address &&
                activeSponsor.toLowerCase() !== address.toLowerCase() &&
                (activeSponsorData?.avatar || activeSponsorData?.name) && (
                  <IconButton className="totalPriceH2">
                    <img
                      src={activeSponsorData?.avatar || avatar}
                      alt={activeSponsorData?.name || "Sponsor"}
                      className="avatarRespons"
                    />
                  </IconButton>
                )}
            </div>
          </div>

          {contractSponsor &&
            sponsorFromApi &&
            contractSponsor !== sponsorFromApi && (
              <Alert
                variant="outlined"
                severity="warning"
                className="outlinedWarn2 bnbAlertH3 "
              >
                You already have a sponsor on-chain. The referral link will not
                be changed.
              </Alert>
            )}

          {validatedSponsor.address &&
            !validatedSponsor.valid &&
            !contractSponsor && (
              <Alert
                variant="outlined"
                severity="warning"
                className="outlinedWarn2 bnbAlertH3"
              >
                The provided sponsor is not verified on-chain. Purchase will
                proceed without a sponsor.
              </Alert>
            )}

          {error && (
            <Alert severity="error" className="mt-3">
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" className="mt-3">
              {success}
            </Alert>
          )}

          <div className="mt-4">
            <span
              onClick={loading ? undefined : handlePurchase}
              className={`presale-submit-btn mt-2 mb-3 ${
                loading ? "disabled" : ""
              }`}
            >
              {loading ? "Processing..." : "Submit"}
            </span>
          </div>
        </div>
      </Col>
    </Row>
  );
}

export default Buy;
